/* eslint-disable @typescript-eslint/no-explicit-any */
import S3 from 'aws-sdk/clients/s3';
import { useCallback, useState } from 'react';
import { Path, PathValue, UseFormSetValue } from 'react-hook-form';
import { s3DOConfig } from 'utils/awsS3.config';
import { uid } from 'utils/generateUID';

const DEFAULT_PATH = 'temporary';
const ACL = 'public-read';

type UseS3ImageUploaderArgs<T extends Record<string, any>> =
  | { onLoad: (data: Partial<Record<keyof T, string>>) => void; setValue?: undefined }
  | { onLoad?: undefined; setValue: UseFormSetValue<T> };

export const useS3ImageUploader = <T extends Record<string, any>>({ onLoad, setValue }: UseS3ImageUploaderArgs<T>) => {
  const [isLoading, setIsLoading] = useState(false);

  const uploadImage = useCallback(async (file: File, imageField: keyof T, path?: string) => {
    setIsLoading(true);
    const s3 = new S3(s3DOConfig);
    const fileType = file.name.split('.').slice(-1)[0];
    const fileName = `${uid()}.${fileType}`;
    const params: S3.PutObjectRequest = {
      Body: file,
      Bucket: s3DOConfig.bucketName || '',
      Key: `${path || DEFAULT_PATH}/${fileName}`,
      ACL,
      ContentType: file.type,
      ContentLength: file.size,
    };
    await s3
      .upload(params)
      .promise()
      .then(({ Location }) => {
        if (setValue) setValue(imageField as Path<T>, Location as PathValue<T, Path<T>>);
        else if (onLoad) onLoad({ [imageField]: Location } as Partial<Record<keyof T, string>>);
      })
      .catch((err) => {
        throw `Error while uploading image\n${JSON.stringify(err)}`;
      })
      .finally(() => setIsLoading(false));
  }, []);
  return { isLoading, uploadImage };
};
